import type { EPCR } from '@/types';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  CHIEF_COMPLAINTS,
  PROVIDER_IMPRESSIONS,
  SIGNS,
  SYMPTOMS,
  type ClinicalGroup,
} from '@/lib/clinical-dictionaries';
import { suggestImpressionsForComplaint } from '@/lib/complaint-impression-map';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoChiefComplaintFields } from '@/lib/demo-data';
import { FanOutChips } from '@/components/FanOutChips';
import { cn } from '@/lib/cn';
import { Sparkles } from 'lucide-react';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

/**
 * Dispatch reasons — wrapped into ClinicalGroup shape for SearchableSelect.
 * Ordered alphabetical; NEMSIS eSituation.07 compliant.
 */
const DISPATCH_REASONS: ClinicalGroup[] = [
  {
    category: 'NEMSIS Dispatch Reasons',
    options: [
      'Abdominal Pain', 'Allergic Reaction', 'Altered Mental Status',
      'Animal Bite', 'Assault', 'Back Pain', 'Breathing Problem',
      'Burns', 'Carbon Monoxide / Hazmat', 'Cardiac Arrest',
      'Chest Pain', 'Choking', 'Convulsions / Seizure',
      'Diabetic Problem', 'Drowning', 'Electrocution',
      'Eye Problem', 'Fall', 'Headache', 'Heart Problem',
      'Heat / Cold Exposure', 'Hemorrhage / Laceration',
      'Overdose / Ingestion', 'Pregnancy / Childbirth',
      'Psychiatric / Behavioral', 'Sick Person', 'Stab / Gunshot',
      'Stroke / CVA', 'Traffic Accident', 'Unconscious / Fainting',
      'Unknown Problem',
    ].map((label) => ({ label })),
  },
];

const SAFETY_FLAGS: string[] = [
  'Violence / Weapons', 'Infection Risk', 'Hazmat Exposure',
  'Unstable Structure', 'Downed Power Lines', 'Animal Hazard',
  'Law Enforcement Requested', 'Active Shooter',
];

export function DispatchSceneTab({ epcr, onUpdate }: Props) {
  const toggleSafety = (flag: string) => {
    const current = epcr.scene_safety_flags;
    const updated = current.includes(flag)
      ? current.filter((f) => f !== flag)
      : [...current, flag];
    onUpdate({ scene_safety_flags: updated });
  };

  // signs / symptoms may not be in the typed EPCR yet — fall back to
  // the assessments array's loose shape until the schema migration
  // lands. Reading/writing via index signature keeps us forward-compat.
  const signs: string[] = (epcr as unknown as { signs?: string[] }).signs ?? [];
  const symptoms: string[] = (epcr as unknown as { symptoms?: string[] }).symptoms ?? [];

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex justify-end">
        <DemoFillButton onFill={() => onUpdate(demoChiefComplaintFields())} />
      </div>

      {/* Dispatch Reason */}
      <Field label="Dispatch Reason" hint="NEMSIS eSituation.07">
        <SearchableSelect
          mode="single"
          groups={DISPATCH_REASONS}
          value={epcr.dispatch_reason}
          onChange={(v) => onUpdate({ dispatch_reason: v })}
          disabled={epcr.locked}
          placeholder="Search dispatch reasons…"
        />
        <FanOutChips capture="incident.dispatch_reason" />
      </Field>

      {/* Chief Complaint — searchable, with free-text fallback */}
      <Field label="Chief Complaint" hint="Patient-stated reason for call. Free-text allowed.">
        <SearchableSelect
          mode="single"
          groups={CHIEF_COMPLAINTS}
          value={epcr.chief_complaint}
          onChange={(v) => onUpdate({ chief_complaint: v })}
          disabled={epcr.locked}
          placeholder="e.g. Chest Pain, MVC, Fall…"
        />
        <FanOutChips capture="situation.chief_complaint" />
      </Field>

      {/* Primary Impression — full NEMSIS code list, medical + trauma.
          When a chief complaint is set, a one-tap suggestion strip
          surfaces the 3 most-likely impressions so the crew doesn't
          re-search the picker for the obvious answer. */}
      <Field label="Primary Impression" hint="Provider working diagnosis. NEMSIS eSituation.11.">
        <ImpressionSuggestions
          complaint={epcr.chief_complaint}
          currentImpression={epcr.primary_impression}
          disabled={epcr.locked}
          onApply={(v) => onUpdate({ primary_impression: v })}
        />
        <SearchableSelect
          mode="single"
          groups={PROVIDER_IMPRESSIONS}
          value={epcr.primary_impression}
          onChange={(v) => onUpdate({ primary_impression: v })}
          disabled={epcr.locked}
          placeholder="Search impressions…"
        />
        <FanOutChips capture="situation.primary_impression" />
      </Field>

      {/* Secondary Impressions — multi */}
      <Field label="Secondary Impressions" hint="Additional working diagnoses. Add as many as apply.">
        <SearchableSelect
          mode="multi"
          groups={PROVIDER_IMPRESSIONS}
          value={epcr.secondary_impressions}
          onChange={(v) => onUpdate({ secondary_impressions: v })}
          disabled={epcr.locked}
          placeholder="Type to add an impression…"
        />
        <FanOutChips capture="situation.secondary_impressions" />
      </Field>

      {/* Signs (provider observed) */}
      <Field label="Signs" hint="Findings you observed on exam.">
        <SearchableSelect
          mode="multi"
          groups={SIGNS}
          value={signs}
          onChange={(v) => onUpdate({ ...(epcr as object), signs: v } as Partial<EPCR>)}
          disabled={epcr.locked}
          placeholder="Search signs…"
        />
      </Field>

      {/* Symptoms (patient reported) */}
      <Field label="Symptoms" hint="Patient-reported symptoms.">
        <SearchableSelect
          mode="multi"
          groups={SYMPTOMS}
          value={symptoms}
          onChange={(v) => onUpdate({ ...(epcr as object), symptoms: v } as Partial<EPCR>)}
          disabled={epcr.locked}
          placeholder="Search symptoms…"
        />
      </Field>

      {/* Scene Safety */}
      <Field label="Scene Safety Flags" hint="Tap flags that applied on arrival.">
        <div className="flex flex-wrap gap-1.5">
          {SAFETY_FLAGS.map((flag) => (
            <button
              key={flag}
              type="button"
              onClick={() => toggleSafety(flag)}
              disabled={epcr.locked}
              className={cn(
                'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors border',
                epcr.scene_safety_flags.includes(flag)
                  ? 'bg-ems-red text-white border-red-500'
                  : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800',
              )}
            >
              {flag}
            </button>
          ))}
        </div>
        {epcr.scene_safety_flags.length === 0 && (
          <p className="mt-1 text-[10px] text-slate-500">No hazards flagged — scene clear</p>
        )}
      </Field>
    </div>
  );
}

/** Compact, vertically-stacked field row — label on top, control under. */
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="data-label mb-1">
        {label}
      </div>
      {children}
      {hint && <p className="mt-1 text-[10px] text-slate-500">{hint}</p>}
    </section>
  );
}

/** One-tap impression suggestions driven off the chief complaint. */
function ImpressionSuggestions({
  complaint, currentImpression, disabled, onApply,
}: {
  complaint: string | null;
  currentImpression: string | null;
  disabled?: boolean;
  onApply: (v: string) => void;
}) {
  if (!complaint) return null;
  const suggestions = suggestImpressionsForComplaint(complaint);
  if (suggestions.length === 0) return null;
  return (
    <div className="flex items-center flex-wrap gap-1 mb-1.5">
      <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-blue-300 mr-0.5">
        <Sparkles size={11} />
        likely
      </span>
      {suggestions.map((s) => {
        const isCurrent = s === currentImpression;
        return (
          <button
            key={s}
            type="button"
            disabled={disabled}
            onClick={() => onApply(s)}
            className={cn(
              'px-2 py-0.5 text-[11px] border transition-colors',
              isCurrent
                ? 'border-blue-400 bg-blue-600 text-white'
                : 'border-blue-700 bg-blue-950/40 text-blue-200 hover:bg-blue-900/40',
            )}
            title="One-tap apply to Primary Impression"
          >
            {s}
          </button>
        );
      })}
    </div>
  );
}
